<template>
  <section>
    <div class="auth-form">
      <form class="form" @submit.prevent="submitSettings">
        <h1 class="form__title">{{ $t('CP.fillInYourProfile') }}</h1>
        <div class="form__fields">
          <image-upload @loadImage="loadImage" :value="imageUrl" isAvatar />
          <div v-if="$v.$dirty && !imageUrl" class="invalid-message mt-2">
            <span>
              {{ $t('validateText.avatarRequired') }}
            </span>
          </div>
          <input-app
              :label-text="$t('CP.name')"
              type="text"
              v-model="firstName"
              :error="$v.$dirty && $v.firstName.$anyError"
          />
          <div v-if="$v.$dirty && $v.firstName.$error" class="invalid-message">
            <span v-if="!$v.firstName.required">
              {{ $t('validateText.firstNameRequired') }}
            </span>
          </div>
          <input-app
              :label-text="$t('CP.surname')"
              type="text"
              v-model="lastName"
              :error="$v.$dirty && $v.lastName.$anyError"
          />
          <div v-if="$v.$dirty && $v.lastName.$error" class="invalid-message">
            <span v-if="!$v.lastName.required">
              {{ $t('validateText.lastNameRequired') }}
            </span>
          </div>
          <div class="input-container">
            <app-select-small
              no-flip
              full-width
              class="settings-form__input select-currency"
              @changeSelect="(val) => currency = val"
              :indexes="currencySelectInputs.indexes"
              :keys="currencySelectInputs.keys"
              :values="currencySelectInputs.values"
              :default="user.currency"
              outlined
              :label="$t('CP.currency')"
            />
          </div>
          <div class="input-container">
            <div class="settings-form__input settings-form__input_gender">
              <app-radio-button-small
                class="gender-radio"
                @radioChange="(val) => gender = val"
                name="gender"
                :message="$t('CP.male')"
                value="M"
                :active="gender === 'M'"
              />
              <app-radio-button-small
                class="gender-radio"
                @radioChange="(val) => gender = val"
                name="gender"
                :message="$t('CP.female')"
                value="F"
                :active="gender === 'F'"
              />
            </div>
          </div>
          <div class="form-buttons">
            <button-app
              class="form-button form-button_cancel"
              @clicked="logout"
              color-scheme="gray"
              type="button">
              {{ $t('CP.exit') }}
            </button-app>
            <button-app
              class="form-button form-button_save"
              color-scheme="blue"
              type="submit"
            >
              {{ $t('CP.save') }}
            </button-app>
          </div>
        </div>
      </form>
    </div>
  </section>
</template>

<script>
/* eslint-disable camelcase */
import { mapState } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { UPDATE_USER_REQUEST } from '@/store/user/types';
import { AUTH_LOGOUT } from '@/store/auth/types';
import { SET_CROPPER_INFO } from '@/store/operations/types';
import AppSelectSmall from '../components/AppSelectSmall/AppSelectSmall';
import AppRadioButtonSmall from '../components/AppRadioButtonSmall/AppRadioButtonSmall';
import InputApp from '../components/Input/InputApp';
import ButtonApp from '../components/Button/ButtonApp';
import ImageUpload from '../components/ImageUpload/ImageUpload';

export default {
  name: 'InitialUserDataForm',
  components: {
    ButtonApp,
    InputApp,
    AppRadioButtonSmall,
    AppSelectSmall,
    ImageUpload,
  },
  validations: {
    firstName: { required },
    lastName: { required },
    gender: { required },
    currency: { required },
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      gender: '',
      currency: '',
      imageUrl: '',
      imageErrorMessage: '',
      showErrorMessage: false,
    };
  },
  mounted() {
    this.gender = this.user.gender;
    this.currency = this.user.currency;
    this.firstName = this.userNames.firstName;
    this.lastName = this.userNames.lastName;
    this.imageUrl = this.user.avatar_url;
  },
  computed: {
    ...mapState({
      user: (state) => state.user.user,
      currencies: (state) => state.currency.currencies,
      cropperInfo: (state) => state.operations.cropperInfo,
    }),
    userNames() {
      try {
        const names = this.user.full_name.split(' ');
        const firstName = names.shift();
        return {
          firstName,
          lastName: names.join(' '),
        };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('cannot properly read full name: ', e.message);
        return {
          firstName: '',
          lastName: '',
        };
      }
    },
    currencySelectInputs() {
      const indexes = [];
      const keys = [];
      const values = [];
      this.currencies.forEach((currency) => {
        indexes.push(currency.id);
        keys.push(currency.currency);
        values.push(currency.currency);
      });
      return {
        indexes,
        keys,
        values,
      };
    },
  },
  watch: {
    cropperInfo(next) {
      if (next && next.destination) {
        this.imageUrl = next.destination;
        this.isImageUrlChanged = true;
      }
    },
  },
  methods: {
    async logout() {
      await this.$store.dispatch(AUTH_LOGOUT);
    },
    async submitSettings() {
      this.showErrorMessage = false;
      this.$v.$touch();
      if (this.$v.$invalid || !this.imageUrl) {
        return;
      }
      let full_name = `${this.firstName} ${this.lastName}`.trim();
      const avatar_url = this.imageUrl.split('base64,')[1];
      if (!full_name) full_name = null;
      const { gender, currency } = this;
      await this.$store.dispatch(UPDATE_USER_REQUEST, {
        full_name,
        gender,
        currency,
        avatar_url,
      })
        .then(() => {
          this.$v.$reset();
          if (this.$route.path !== '/events') this.$router.push('/events');
        })
        .catch((e) => {
          this.$v.$reset();
          // eslint-disable-next-line no-console
          console.log('could not submit: ', e.message);
        });
    },
    loadImage(file) {
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          const img = new Image();
          img.src = reader.result.toString();
          img.onload = () => {
            let imageError = false;
            if (img.width < 70 || img.width > 10000
              || img.height < 70 || img.height > 10000 || file.size > 5242880) {
              this.imageErrorMessage = this.$t('CP.errorOutOfBoundsPictureCrop');
              // eslint-disable-next-line no-console
              console.log('image size should be between 70x70 and 10000x10000 px',
                'current size: ', img.width, img.height);
              imageError = true;
            }
            if (imageError) return;
            this.$store.commit(SET_CROPPER_INFO, {
              src: img.src,
            });
            if (file instanceof File) {
              this.$bvModal.show('cropper');
            } else {
              this.imageUrl = img.src;
            }
          };
        };
        reader.readAsDataURL(file);
        return null;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e.message);
        throw new Error(e.message);
      }
    },
  },
};
</script>

<style scoped>
.settings-form__input_gender {
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  padding: 0 0 6px;
  min-height: 50px;
}
.gender-radio {
  margin-right: 10px;
}
.form-buttons {
  display: flex;
  padding: 20px 0 30px;
}
.form-button {
  flex: 1;
  height: 44px;
  margin: 0;
}
.form-button_cancel {
  margin: 0 8px 0 0;
}
</style>
